import { useEffect, useLayoutEffect, useRef, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import { MODAL_FONT } from "./DocumentoConVisor";
import { marcarArchivoReescrito, normalizarGiro, versionArchivo } from "./rotarDocumento";
import { rotarDocumentoLegacy } from "./rotarDocumentoLegacy";

// Botones de rotar sin fondo ni borde y con el ícono centrado explícitamente
// (mismo estilo que en DocumentoConVisor).
const rotateButtonStyle = {
  width: "28px", height: "28px", minWidth: "28px", padding: 0, margin: 0, boxSizing: "border-box", borderRadius: "6px",
  border: "none", background: "transparent", cursor: "pointer", color: "#0065DD", lineHeight: 0,
  display: "inline-grid", placeItems: "center",
} as const;

/**
 * Visor (iframe del visor externo de documentos) del modelo antiguo con
 * rotación izquierda/derecha. El giro se previsualiza rotando el iframe y se
 * guarda con "Guardar rotación", que sobrescribe el archivo en el backend
 * (ver rotarDocumentoLegacy); después recarga el visor.
 */
const VisorDocumentoLegacy = ({
  src,
  personaDNI,
  documentoId,
  height = 400,
  title = "Documentación de personas",
}: {
  src: string;
  personaDNI?: string;
  documentoId?: string;
  height?: number;
  title?: string;
}) => {
  const [giro, setGiro] = useState(0);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  // Re-render tras guardar; la versión real vive en el registro compartido.
  const [, setVersion] = useState(0);
  const claveArchivo = `legacy:${personaDNI ?? ""}:${documentoId ?? ""}`;
  const version = versionArchivo(claveArchivo);

  const giroNormalizado = normalizarGiro(giro);
  const deLado = giroNormalizado === 90 || giroNormalizado === 270;
  const puedeRotar = Boolean(personaDNI && documentoId);

  // Al cambiar de documento se descarta el giro sin guardar.
  useEffect(() => { setGiro(0); setError(null); setAviso(null); }, [documentoId, personaDNI]);

  const contenedorRef = useRef<HTMLDivElement | null>(null);
  const [ancho, setAncho] = useState(0);
  useLayoutEffect(() => {
    const el = contenedorRef.current;
    if (!el) return;
    setAncho(el.clientWidth);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setAncho(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const rotar = (delta: number) => { setError(null); setAviso(null); setGiro((g) => g + delta); };

  const guardar = async () => {
    if (!personaDNI || !documentoId || guardando || giroNormalizado === 0) return;
    setGuardando(true);
    setError(null);
    try {
      await rotarDocumentoLegacy({ personaDNI, documentoId, giro: giroNormalizado });
      marcarArchivoReescrito(claveArchivo);
      setGiro(0);
      setVersion((v) => v + 1);
      setAviso("Rotación guardada.");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setGuardando(false);
    }
  };

  const srcFinal = version ? `${src}${src.includes("?") ? "&" : "?"}_v=${version}` : src;

  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {puedeRotar && (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", padding: "6px 10px", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: "8px 8px 0 0" }}>
          <span style={{ fontFamily: MODAL_FONT, fontSize: "11px", color: "#64748b", fontWeight: 500 }}>Rotar</span>
          <button type="button" title="Rotar a la izquierda" aria-label="Rotar a la izquierda" disabled={guardando} onClick={() => rotar(-90)} style={rotateButtonStyle}>
            <RotateLeftIcon style={{ fontSize: 19, display: "block" }} />
          </button>
          <button type="button" title="Rotar a la derecha" aria-label="Rotar a la derecha" disabled={guardando} onClick={() => rotar(90)} style={rotateButtonStyle}>
            <RotateRightIcon style={{ fontSize: 19, display: "block" }} />
          </button>
          {giroNormalizado !== 0 && (
            <>
              <button
                type="button"
                onClick={() => { setGiro(0); setError(null); }}
                disabled={guardando}
                style={{ padding: "3px 8px", borderRadius: "6px", border: "1px solid #e2e8f0", background: "#fff", cursor: guardando ? "not-allowed" : "pointer", fontFamily: MODAL_FONT, fontSize: "11px", color: "#475569" }}
              >
                Deshacer
              </button>
              <button
                type="button"
                onClick={guardar}
                disabled={guardando}
                style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "3px 10px", borderRadius: "6px", border: "none", background: guardando ? "#93c5fd" : "#0065DD", color: "#fff", cursor: guardando ? "wait" : "pointer", fontFamily: MODAL_FONT, fontSize: "11px", fontWeight: 600 }}
              >
                {guardando && <CircularProgress size={10} thickness={6} sx={{ color: "#fff" }} />}
                {guardando ? "Guardando…" : "Guardar rotación"}
              </button>
              <span style={{ fontFamily: MODAL_FONT, fontSize: "11px", color: "#b45309", fontWeight: 600 }}>
                Rotación sin guardar ({giroNormalizado}°)
              </span>
            </>
          )}
          {aviso && giroNormalizado === 0 && (
            <span style={{ fontFamily: MODAL_FONT, fontSize: "11px", color: "#15803d", fontWeight: 600 }}>{aviso}</span>
          )}
          {error && <span style={{ fontFamily: MODAL_FONT, fontSize: "11px", color: "#b91c1c" }}>{error}</span>}
        </div>
      )}
      <div
        ref={contenedorRef}
        style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: `${height}px`, overflow: "hidden" }}
      >
        <iframe
          key={version}
          src={srcFinal}
          title={title}
          frameBorder="0"
          allowFullScreen
          style={{
            flexShrink: 0,
            border: "none",
            // Girado de lado se intercambian ancho y alto para que, ya rotado,
            // ocupe exactamente el área del visor.
            width: deLado ? `${height}px` : "100%",
            height: deLado ? `${ancho || 800}px` : `${height}px`,
            transform: `rotate(${puedeRotar ? giro : 0}deg)`,
            transition: "transform 0.2s ease",
          }}
        />
      </div>
    </div>
  );
};

export default VisorDocumentoLegacy;
